import PageTitle from "../../components/PageTitle";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";
import PageContent from "../../components/PageContent";
import useApi from "../../hooks/ApiHook";
import { useEffect, useState } from "react";
import If from "../../components/If";
import Loading from "../../components/Loading";
import usePaginator from "../../hooks/PaginatorHook";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tag } from 'primereact/tag';
import useToast from "../../hooks/ToastHook";
import InputSearchSidebar from "../../components/InputSearchSidebar";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';

export default function() {

    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const [ connections, setConnections ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const { page, limit, setTotal, Paginator } = usePaginator();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ expandedRows, setExpandedRows ] = useState(null);
    const [ clientWatingRelationship, setClientWaitingRelationship ] = useState();
    
    useEffect(() => {
        setLoading(true);
        retrieve();
    }, [page, limit]);

    const retrieve = () => {
        api.get(`clients?page=${page}&limit=${limit}`).then(({ data }) => {
            setConnections(data.data);
            setTotal(data.total);
            setSearchParams({ page, limit });
        }).finally(() => setLoading(false));
    }

    const selectIndustryToRelationship = industry => {
        if (!clientWatingRelationship) {
            return;
        }
        setLoading(true);
        setClientWaitingRelationship(null);
        api.post(`clients/${clientWatingRelationship.id}/relationships`, { industry: { id: industry.id } }).then(() => {
            toast.showSuccess("Relacionamento enviado para aprovação da indústria");
            retrieve();
        }).catch(e => {
            toast.showAxiosError(e);
        }).finally(() => {
            setLoading(false);
        });
    }

    const actions = client => (
        <>
            <Button icon="pi pi-pencil" className="ml-1" title="Editar" onClick={() => navigate('./' + client.id)}/>
        </>
    );

    const titleButtons = (
        <Button label="Adicionar" onClick={() => navigate('./new')}/>
    );

    const statusColumnBody = (relationship) => relationship.status === 'WAITING'
        ? <Tag severity="warning" value="AGUARDANDO"/>
        : <Tag value="ATIVO"/>;

    const expansionTemplate = client => (
        <div className="surface-100 p-1">
            <DataTable value={client.relationships} rowClassName="surface-100" emptyMessage="Nada encontrado">
                <Column field="industry.commercialName" header="Indústria"></Column>
                <Column body={statusColumnBody} header="Status"></Column>
            </DataTable>
            <div className="p-2 flex justify-content-end">
                <Button icon="pi pi-plus" className="ml-1" title="Adicionar relacionamento" onClick={() => setClientWaitingRelationship(client)}/>
            </div>
        </div>
    );

    return (
        <div>
            <PageTitle title="Clientes" subtitle="Estabeleça conexões com seus clientes" end={titleButtons}/>
            <PageContent>
                <If condition={loading}>
                    <Loading/>
                </If>
                <If condition={!loading}>
                    <DataTable 
                        value={connections} 
                        selectionMode="single" 
                        emptyMessage="Nada encontrado"
                        expandedRows={expandedRows}
                        rowExpansionTemplate={expansionTemplate}
                        onRowToggle={e => setExpandedRows(e.data)}>
                        <Column expander={true} style={{ width: '5rem' }} />
                        <Column field="commercialName" header="Empresa"></Column>
                        <Column field="document" header="CPF/CNPJ"></Column>
                        <Column field="relationships.length" header="Conexões" align="right"></Column>
                        <Column body={actions} header="Ações" align='right'></Column>
                    </DataTable>
                    <Paginator/>
                </If>
            </PageContent>
            <SearchIndustrySidebar
                client={clientWatingRelationship}
                onHide={() => setClientWaitingRelationship(null)}
                onSelect={selectIndustryToRelationship}/>
        </div>
    );
}

const SearchIndustrySidebar = ({ client, onSelect, onHide }) => {

    const api = useApi();
    const [ open, setOpen ] = useState(false);
    const [ options, setOptions ] = useState([]);
    const [ searchQuery, setSearchQuery ] = useState('');
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        setOpen(!!client);
    }, [client]);

    useEffect(() => {
        retrieveData();
    }, [open]);

    const retrieveData = () => {
        if (!open) return;
        setLoading(true);
        const params = { "industry.commercialName[contains]": searchQuery, limit: 30, page: 0 };
        api.get(`industries`, { params }).then(({ data }) => {
            setOptions(data.data.filter(d => !client.relationships.some(r => r.industry.id === d.id)));
        }).finally(() => setLoading(false));
    }

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            setOptions([]);
            retrieveData();
        }, 500);
        return () => clearTimeout(timeOutId);
    }, [searchQuery]);

    const select = industry => {
        confirmDialog({
            message: 'Ao solicitar uma conexão com uma indústria, a mesma será notificado e poderá aceitar ou não a solicitação. Enquanto isso, a conexão aparecerá com o status de AGUARDANDO na listagem de conexões.',
            header: 'Atenção',
            icon: 'pi pi-exclamation-triangle',
            draggable: false, 
            className: "col-12 md:col-8 lg:col-4 xl:col-4",
            acceptLabel: 'Continuar',
            rejectLabel: 'Cancelar',
            accept: () => onSelect(industry)
        });
    }
    
    return (
        <Sidebar visible={open} className="md:w-30rem" position="right" onHide={onHide}>
            <div className="h-full flex flex-column">
                <h2>Buscar indústria</h2>
                <span className="p-input-icon-right">
                    {loading && <i className="pi pi-spin pi-spinner" />}
                    <InputText 
                        className="w-full" 
                        onChange={e => setSearchQuery(e.target.value)} 
                        value={searchQuery}/>
                </span>
                <div className="w-full flex-grow-1 mt-4 max-w-full flex-shrink-1 overflow-y-hidden overflow-y-scroll">
                    {options.map(o => (
                        <div onClick={() => select(o)} className="p-3 cursor-pointer hover:surface-hover">
                            <div className="font-semibold">{o.commercialName}</div>
                            <div className="text-sm mt-2" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{o.document}</div>
                        </div>
                    ))}
                </div>
            </div>
            <ConfirmDialog/>
        </Sidebar>
    );

}